'use client';

import Image from 'next/image';
import { useRef, useState } from 'react';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';

export interface Review {
  img: string;
  review: string;
  name: string;
}

interface ReviewsSliderProps {
  reviews: Review[];
  variant?: 'comix' | 'ornaments';
  /** When true, arrows sit flush at the edges of the container (instead of inset) and the
   *  slide track loses its inner side padding, so cards reach the same edges as the rest of
   *  the surrounding grid. Opt-in so the default (ornaments) presentation is unaffected. */
  edgeArrows?: boolean;
}

export default function ReviewsSlider({ reviews, variant = 'comix', edgeArrows = false }: ReviewsSliderProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const updateState = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <div className="relative w-full">
      {/* Left arrow. Desktop-only hover/press feedback: hover tints the bg/icon pink and
          scales down slightly; active (click/hold) scales down further, then springs
          back to the hover size on release — all via CSS transitions, no JS needed. */}
      <button
        onClick={() => swiperRef.current?.slidePrev()}
        className={`hidden dt:flex absolute ${edgeArrows ? 'left-0' : 'left-4'} top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white text-[#333333] shadow-md transition-all duration-200 hover:scale-95 hover:bg-[#FFF0F6] hover:text-[#F52F81] hover:shadow-lg active:scale-90 active:duration-100 ${isBeginning ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        aria-label="Previous"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      {/* Right arrow — same hover/press treatment as the left arrow. */}
      <button
        onClick={() => swiperRef.current?.slideNext()}
        className={`hidden dt:flex absolute ${edgeArrows ? 'right-0' : 'right-4'} top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white text-[#333333] shadow-md transition-all duration-200 hover:scale-95 hover:bg-[#FFF0F6] hover:text-[#F52F81] hover:shadow-lg active:scale-90 active:duration-100 ${isEnd ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        aria-label="Next"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      <Swiper
        onSwiper={(swiper) => { swiperRef.current = swiper; updateState(swiper); }}
        onSlideChange={updateState}
        onReachBeginning={(swiper) => setIsBeginning(true)}
        onReachEnd={(swiper) => setIsEnd(true)}
        slidesPerView={1.2}
        centeredSlides={false}
        spaceBetween={12}
        initialSlide={0}
        // Mobile-only: the edge-to-edge (edgeArrows) mobile track otherwise starts the
        // first card flush against the viewport edge — 16px keeps it clear without
        // affecting the gap between subsequent slides.
        slidesOffsetBefore={edgeArrows ? 16 : 0}
        breakpoints={{
          // 3 equal-width slides filling the container, matching How It Works' 3-column
          // row above (dt:flex-1 + dt:gap-[2.4%]) — 'auto' + a fixed card width doesn't
          // divide the 1040px container evenly and was overflowing the last card.
          800: {
            slidesPerView: 3,
            spaceBetween: 25,
            centeredSlides: false,
            slidesOffsetBefore: 0,
          },
        }}
        // Bottom padding + matching negative margin give shadow-lg room to render fully
        // before the swiper's own overflow-hidden (needed to clip off-screen slides
        // horizontally) cuts it off, without changing the slider's total footprint.
        // Inline style (not a Tailwind class) because Swiper's own base CSS (`.swiper {
        // padding: 0 }`, from `swiper/css`) has equal selector specificity and loads after
        // Tailwind's utilities — it silently won the padding-bottom cascade, so the class-based
        // version of this fix had zero effect (margin still applied, padding didn't) and the
        // shadow was clipped flush with no breathing room at all.
        className={edgeArrows ? '' : 'md:px-16'}
        style={{ paddingBottom: 26, marginBottom: -26 }}
      >
        {reviews.map((item, i) => (
          <SwiperSlide key={i} style={{ width: undefined }}>
            {variant === 'ornaments' ? (
              <div className="flex flex-col">
                <div className="rounded-t-3xl overflow-hidden flex-grow flex items-center relative aspect-square">
                  <Image
                    src={item.img}
                    alt={item.name}
                    fill
                    sizes="(max-width: 768px) 90vw, 360px"
                    className="object-contain"
                    quality={80}
                    priority={i === 0}
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                </div>
                <div className="rounded-b-3xl p-6 relative flex-shrink-0" style={{ minHeight: '180px' }}>
                  <div className="absolute inset-0 bg-white opacity-20 rounded-b-3xl" />
                  <div className="relative z-10 text-center">
                    <p className="text-white leading-relaxed mb-4" style={{ fontSize: '18px' }}>
                      {item.review}
                    </p>
                    <p className="text-white font-medium" style={{ fontSize: '16px' }}>{item.name}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full">
                <div className="rounded-[25px] overflow-hidden shadow-lg">
                  <div className="relative h-[274px] overflow-hidden">
                    {/* No `priority` — every review photo (including the first) lazy-loads,
                        deferring the whole "Join our community" carousel until it's
                        actually scrolled near instead of competing with above-the-fold
                        assets on first paint. */}
                    <Image
                      src={item.img}
                      alt={item.name}
                      fill
                      loading="lazy"
                      className="object-cover"
                      sizes="(max-width: 768px) 90vw, 360px"
                    />
                  </div>
                  <div className="bg-white rounded-b-[25px] px-5 py-4 shadow-sm">
                    <p className="text-[#333333] text-[15px] leading-[1.5] mb-2.5">
                      {item.review}
                    </p>
                    <p className="text-[#333333] text-[15px] font-medium">{item.name}</p>
                  </div>
                </div>
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
